import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Stack,
  Card,
  CardContent,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridActionsCellItem } from '@mui/x-data-grid';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Email from '@mui/icons-material/Email';
import Phone from '@mui/icons-material/Phone';
import Key from '@mui/icons-material/Key';
import LockReset from '@mui/icons-material/LockReset';
import { AdminUser, AdminPermissions, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';

interface AdminUsersManagementProps {
  permissions: CrudPermissions;
}

const darkButtonSx = {
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  },
};

const defaultCrudPermissions: CrudPermissions = {
  create: false,
  read: false,
  update: false,
  delete: false,
};

const defaultPermissions: AdminPermissions = {
  canViewMetrics: false,
  students: { ...defaultCrudPermissions },
  parents: { ...defaultCrudPermissions },
  community: { ...defaultCrudPermissions },
  fees: { ...defaultCrudPermissions },
  feeAssessment: { ...defaultCrudPermissions },
  orders: { ...defaultCrudPermissions },
  categories: { ...defaultCrudPermissions },
  courses: { ...defaultCrudPermissions },
  schoolYears: { ...defaultCrudPermissions },
  questionnaires: { ...defaultCrudPermissions },
  disclaimers: { ...defaultCrudPermissions },
  notifications: { ...defaultCrudPermissions },
  buildings: { ...defaultCrudPermissions },
  schools: { ...defaultCrudPermissions },
  canBulkImport: false,
  admins: { ...defaultCrudPermissions },
  workers: { ...defaultCrudPermissions },
};

interface PermissionSection {
  key: keyof Omit<AdminPermissions, 'canViewMetrics' | 'canBulkImport'>;
  label: string;
}

const permissionSections: { [category: string]: PermissionSection[] } = {
  'User Management': [
    { key: 'students', label: 'Students' },
    { key: 'parents', label: 'Parents' },
    { key: 'community', label: 'Community Members' },
  ],
  'Financial Management': [
    { key: 'fees', label: 'Fees' },
    { key: 'feeAssessment', label: 'Fee Assessment' },
    { key: 'orders', label: 'Orders' },
    { key: 'categories', label: 'Fee Categories' },
    { key: 'disclaimers', label: 'Fee Disclaimers' },
  ],
  'Academic Management': [
    { key: 'courses', label: 'Courses' },
    { key: 'schoolYears', label: 'School Years' },
  ],
  'Facilities': [
    { key: 'schools', label: 'Schools' },
    { key: 'buildings', label: 'Buildings' },
  ],
  'Communication & Forms': [
    { key: 'notifications', label: 'Notifications' },
    { key: 'questionnaires', label: 'Questionnaires' },
  ],
  'Point of Sale': [
    { key: 'workers', label: 'POS Workers' },
  ],
  'System Administration': [
    { key: 'admins', label: 'Admin Users' },
  ],
};

export const AdminUsersManagement: React.FC<AdminUsersManagementProps> = ({ permissions }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const { adminUsers, setAdminUsers } = useData();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isActive: true,
    isSuperAdmin: false,
    permissions: defaultPermissions,
  });

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      isActive: true,
      isSuperAdmin: false,
      permissions: defaultPermissions,
    });
    setDialogOpen(true);
  };

  const handleEditUser = (user: AdminUser) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      isActive: user.isActive,
      isSuperAdmin: user.isSuperAdmin,
      permissions: user.permissions,
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (user: AdminUser) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      setAdminUsers(adminUsers.filter(u => u.id !== userToDelete.id));
      setSnackbarMessage(`${userToDelete.firstName} ${userToDelete.lastName} has been deleted.`);
      setSnackbarOpen(true);
    }
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleSendPasswordReset = () => {
    if (editingUser) {
      setSnackbarMessage(`Password reset email sent to ${editingUser.email}`);
      setSnackbarOpen(true);
    }
  };

  const handleSave = () => {
    if (editingUser) {
      setAdminUsers(adminUsers.map(u =>
        u.id === editingUser.id
          ? { ...u, ...formData }
          : u
      ));
    } else {
      const newUser: AdminUser = {
        id: `admin-${Date.now()}`,
        ...formData,
        createdAt: new Date(),
      };
      setAdminUsers([...adminUsers, newUser]);
    }
    setDialogOpen(false);
  };

  const handleCrudPermissionChange = (
    section: keyof Omit<AdminPermissions, 'canViewMetrics' | 'canBulkImport'>,
    action: keyof CrudPermissions
  ) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [section]: {
          ...(formData.permissions[section] as CrudPermissions),
          [action]: !(formData.permissions[section] as CrudPermissions)[action],
        },
      },
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'fullName',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => `${row.firstName} ${row.lastName}`,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: 'password',
      headerName: 'Password',
      width: 120,
      renderCell: (params: GridRenderCellParams<AdminUser>) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            color: 'text.secondary',
            bgcolor: 'grey.100',
            px: 1,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          {params.row.password || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 120,
    },
    {
      field: 'isSuperAdmin',
      headerName: 'Role',
      width: 120,
      renderCell: (params: GridRenderCellParams<AdminUser>) => (
        params.row.isSuperAdmin ? (
          <Chip label="Super Admin" color="error" size="small" />
        ) : (
          <Chip label="Admin" color="primary" size="small" />
        )
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams<AdminUser>) => (
        params.row.isActive ? (
          <Chip label="Active" color="success" size="small" />
        ) : (
          <Chip label="Inactive" color="default" size="small" />
        )
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 100,
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        ...(permissions.update ? [
          <GridActionsCellItem
            icon={<Edit />}
            label="Edit"
            onClick={() => handleEditUser(params.row as AdminUser)}
          />,
        ] : []),
        ...(permissions.delete ? [
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => handleDeleteClick(params.row as AdminUser)}
          />,
        ] : []),
      ],
    },
  ];

  const renderMobileCards = () => (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {adminUsers.map((user) => (
        <Card key={user.id} sx={{ position: 'relative', width: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  {user.isSuperAdmin ? (
                    <Chip label="Super Admin" color="error" size="small" />
                  ) : (
                    <Chip label="Admin" color="primary" size="small" />
                  )}
                  {user.isActive ? (
                    <Chip label="Active" color="success" size="small" />
                  ) : (
                    <Chip label="Inactive" color="default" size="small" />
                  )}
                </Stack>
              </Box>
              <Stack direction="row" spacing={0.5}>
                {permissions.update && (
                  <IconButton size="small" onClick={() => handleEditUser(user)}>
                    <Edit fontSize="small" />
                  </IconButton>
                )}
                {permissions.delete && (
                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(user)}>
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
              {user.password && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Key fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      bgcolor: 'grey.100',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                    }}
                  >
                    {user.password}
                  </Typography>
                </Box>
              )}
              {user.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {user.phone}
                  </Typography>
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                Created: {new Date(user.createdAt).toLocaleDateString()}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', lg: 'center' },
        mb: 3,
        gap: 2
      }}>
        <Typography variant="h5" component="h1" fontWeight={600}>
          Admin Users
        </Typography>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddUser}
            sx={{
              ...darkButtonSx,
              width: { xs: '100%', lg: 'auto' },
            }}
          >
            Add Admin User
          </Button>
        )}
      </Box>

      {isMobile ? (
        renderMobileCards()
      ) : (
        <Paper>
          <DataGrid
            rows={adminUsers}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            sx={{ border: 'none' }}
          />
        </Paper>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { width: '100%', m: { xs: 2, sm: 3 } }
        }}
      >
        <DialogTitle>
          {editingUser ? 'Edit Admin User' : 'Add Admin User'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
            >
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Stack>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active Account"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isSuperAdmin}
                    onChange={(e) => setFormData({ ...formData, isSuperAdmin: e.target.checked })}
                  />
                }
                label="Super Admin (Full Access)"
              />
            </Stack>

            {!formData.isSuperAdmin && (
              <Box sx={{ mt: 1 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Configure individual permissions for this admin user
                </Alert>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography>Dashboard & Analytics</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.permissions.canViewMetrics}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: {
                              ...formData.permissions,
                              canViewMetrics: e.target.checked,
                            },
                          })}
                        />
                      }
                      label="View Metrics Dashboard"
                    />
                  </AccordionDetails>
                </Accordion>

                {Object.entries(permissionSections).map(([category, sections]) => (
                  <Accordion key={category}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography>{category}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        {sections.map((section) => (
                          <Box key={section.key}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              {section.label}
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ pl: 2, flexWrap: 'wrap', gap: 1 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={(formData.permissions[section.key] as CrudPermissions).create}
                                    onChange={() => handleCrudPermissionChange(section.key, 'create')}
                                    size="small"
                                  />
                                }
                                label="Create"
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={(formData.permissions[section.key] as CrudPermissions).read}
                                    onChange={() => handleCrudPermissionChange(section.key, 'read')}
                                    size="small"
                                  />
                                }
                                label="Read"
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={(formData.permissions[section.key] as CrudPermissions).update}
                                    onChange={() => handleCrudPermissionChange(section.key, 'update')}
                                    size="small"
                                  />
                                }
                                label="Update"
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={(formData.permissions[section.key] as CrudPermissions).delete}
                                    onChange={() => handleCrudPermissionChange(section.key, 'delete')}
                                    size="small"
                                  />
                                }
                                label="Delete"
                              />
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography>System Operations (Other)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.permissions.canBulkImport}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: {
                              ...formData.permissions,
                              canBulkImport: e.target.checked,
                            },
                          })}
                        />
                      }
                      label="Use Bulk Import"
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: editingUser ? 'space-between' : 'flex-end' }}>
          {editingUser && (
            <Button
              startIcon={<LockReset />}
              onClick={handleSendPasswordReset}
              color="warning"
              variant="outlined"
            >
              Send Password Reset
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" sx={darkButtonSx}>
              Save
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Admin User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
